import {first} from 'lodash';
import {DOCUMENT} from '@angular/common';
import {Logger} from '../../../utils/Logger';
import {Component, Inject, OnInit} from '@angular/core';
import {UserUtil} from '../../../utils/UserUtil';
import {Validator} from '../../../utils/Validator';
import {ActivatedRoute, Router} from '@angular/router';
import {UserService} from '../../../service/user.service';
import {FormGroupUtil} from '../../../utils/FormGroupUtil';
import {UserAddress} from '../../../model/user-address.model';
import {EmployeeType, IncomeToEmployeeTypeConverter} from '../../../model/employee-type.model';
import {Keys, StorageService} from '../../../service/storage.service';
import {UserAddressService} from '../../../service/user-address.service';
import {Income, IncomeType} from '../../../model/eligibility-check/income.model';
import {EligibilityCheckService} from '../../../service/eligibility-check.service';
import {CreditAssessmentService} from '../../../service/credit-assessment.service';
import {SubventionPreferenceService} from '../../../service/subvention-preference.service';
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {EligibilityCheckRequest, Obligations} from '../../../model/eligibility-check/eligibility-check-request.model';
import {LoginHelper} from '../../../helpers/login.helper';
import {BrowserUtil} from '../../../utils/browser.util';
import {MathUtil} from '../../../utils/MathUtil';
import {ChannelPartner} from '../../../model/channel-partner.model';
import {AdvanceEmiOption} from '../../../model/AdvanceEmiPlan';
import * as moment from 'moment';
import {ConfigService} from '../../../service/config.service';

export interface SharedEligibilityCheckForm {
    loanAmount: number;
    income: Income;
    obligations: Obligations;
    partnerId: string;
    requestedAmount?: number;
    advanceEmiOption?: AdvanceEmiOption;
}

@Component({
    selector: 'app-eligibility-check-form',
    templateUrl: './eligibility-check-form.component.html',
    styleUrls: ['./eligibility-check-form.component.css']
})
export class EligibilityCheckFormComponent implements OnInit {
    public static readonly MinimumSalary = 25000;
    public static readonly MinimumAnnualSalary = 500000;
    private static readonly MaximumDateOfBirth = moment().subtract(18, 'years').toDate();
    private static readonly MinimumDateOfBirth = moment().subtract(57, 'years').toDate();
    public user: any;
    public name: string;
    public partnerId: string;
    public loanAmount: number;
    public mobileNumber: string;
    public clearPhoneInfo = false;
    public checkEligibilityForm: any;
    public submissionError: string;
    public loading: boolean;
    public advanceEmiOptions = [];
    public channelPartner: ChannelPartner;

    public EmployeeType = EmployeeType;
    private loginHelper: LoginHelper;
    private document: any;

    public minimumLoanAmount;

    constructor(public formBuilder: FormBuilder, private activatedRoute: ActivatedRoute,
                private subventionPreferenceService: SubventionPreferenceService,
                private userService: UserService, private storageService: StorageService,
                private userAddressService: UserAddressService, private router: Router,
                private eligibilityCheckService: EligibilityCheckService,
                private creditAssessmentService: CreditAssessmentService, loginHelper: LoginHelper,
                public readonly userUtil: UserUtil, private browserUtil: BrowserUtil, private configService: ConfigService, @Inject(DOCUMENT) document: any) {

        this.userUtil = userUtil;
        this.loginHelper = loginHelper;
        this.browserUtil = browserUtil;

        this.minimumLoanAmount = this.configService.getMinimumLoanAmountForEligibilityCheckPartner();

        const employeeTypeFormControl = new FormControl(null, [Validators.required]);
        this.activatedRoute.data.subscribe((data) => {
                this.channelPartner = data.channelPartner;
            }
        );
        const requestedAmountFormControl = new FormControl(null, this.channelPartner.isAdvanceEmiPartner() ? [Validators.required, Validators.max(500000), Validators.min(this.minimumLoanAmount), Validator.denomination] : null);
        const advanceEmiOptionFormControl = new FormControl(null, this.channelPartner.isAdvanceEmiPartner() ?
            [Validators.required, this.minimumBalanceAmount(requestedAmountFormControl)] : null);
        this.checkEligibilityForm = formBuilder.group({
            loanAmount: new FormControl(null, !this.channelPartner.isAdvanceEmiPartner() ? [Validators.required, Validators.max(500000), Validators.min(this.minimumLoanAmount), Validator.denominationOf(1000)] : null),
            income: formBuilder.group({
                employeeType: employeeTypeFormControl,
                amount: new FormControl(null, [Validators.required, this.maxForEmployeeType(employeeTypeFormControl)])
            }),
            emi: new FormControl(null, [Validators.min(0)]),
            rent: new FormControl(null, [Validators.min(0)]),
            dateOfBirth: new FormControl(null, [Validators.required,
                Validator.maxDate(EligibilityCheckFormComponent.MaximumDateOfBirth),
                Validator.minDate(EligibilityCheckFormComponent.MinimumDateOfBirth)]),
            pin: new FormControl(undefined, [Validators.required]),
            city: new FormControl(null, [Validators.required]),
            state: new FormControl(null, [Validators.required]),
            termsPartOne: new FormControl(null, [Validators.required]),
            requestedAmount: requestedAmountFormControl,
            advanceEmiOption: advanceEmiOptionFormControl
        });
        this.document = document;
    }

    public allErrorMessages() {
        return {
            loanAmount: [
                {
                    type: 'required',
                    message: 'Please enter a loan amount'
                },
                {
                    type: 'min',
                    message: `Loan amount should be between Rs ${this.minimumLoanAmount} and Rs 500000`
                },
                {
                    type: 'max',
                    message: `Loan amount should be between Rs ${this.minimumLoanAmount} and Rs 500000`
                },
                {
                    type: 'denomination',
                    message: 'Loan amount should be in multiples of 1,000.'
                }
            ],
            requestedAmount: [
                {
                    type: 'required',
                    message: 'Please enter the required amount'
                },
                {
                    type: 'min',
                    message: `Amount should be between Rs ${this.minimumLoanAmount} and Rs 500000`
                },
                {
                    type: 'max',
                    message: `Amount should be between Rs ${this.minimumLoanAmount} and Rs 500000`
                },
                {
                    type: 'pattern',
                    message: 'Amount should be in multiples of 1,000.'
                }
            ],
            employeeType: [
                {
                    type: 'required',
                    message: 'Please select employee type'
                }
            ],
            emi: [{
                type: 'min',
                message: 'Please enter a valid EMI amount'
            }],
            rent: [{
                type: 'min',
                message: 'Please enter a valid rent amount'
            }],
            advanceEmiOption: [{
                type: 'required',
                message: 'Please select number of EMI’s'
            }, {
                type: 'minimumBalanceAmount',
                message: `For the selected tenure, the loan amount will breach the Rs ${this.minimumLoanAmount} limit. Please select a different tenure.`
            }],
            amount: [
                {
                    type: 'required',
                    message: 'Please enter your income'
                },
                {
                    type: 'minAnnualIncome',
                    message: `Annual Income should be greater than ₹${EligibilityCheckFormComponent.MinimumAnnualSalary}`
                },
                {
                    type: 'minMonthlyIncome',
                    message: `Monthly Income should be greater than Rs ${EligibilityCheckFormComponent.MinimumSalary}`
                }
            ],
            dateOfBirth: [{
                type: 'required',
                message: 'Please select your date of birth'
            }, {
                type: 'max',
                message: 'Please enter a valid date of birth'
            }, {
                type: 'min',
                message: 'Please enter a valid date of birth'
            }],
            pin: [{
                type: 'required',
                message: 'Please enter a valid PIN code'
            },
                {
                    type: 'pattern',
                    message: 'Please enter a valid PIN code'
                }
            ],
            city: [{
                type: 'required',
                message: 'Please enter a city'
            }, {
                type: 'cityListError',
                message: 'Please select a valid option'
            }],
            state: [{
                type: 'required',
                message: 'Please enter the state'
            }],
            termsPartOne: [{
                type: 'required',
                message: 'Please accept the terms to proceed'
            }]
        };
    }

    async ngOnInit(): Promise<void> {
        this.checkForAdvanceEmi();
        const loginToken = this.userUtil.isUserLoggedIn();
        if (loginToken) {
            const userId = this.storageService.getItem(Keys.PROFILE_ID);
            if (userId) {
                this.user = await this.userService.getProfileDetails(userId);
            }
            const state = this.storageService.getObject<{ eligibilityCheckForm: SharedEligibilityCheckForm }>(Keys.EXTRA_OPTIONS_STATE);
            if (this.user && state) {
                const employeeType = IncomeToEmployeeTypeConverter.convert(state.eligibilityCheckForm.income);

                this.checkEligibilityForm.patchValue({
                    loanAmount: state.eligibilityCheckForm.loanAmount,
                    income: {employeeType: employeeType, amount: state.eligibilityCheckForm.income.amount},
                    emi: state.eligibilityCheckForm.obligations.emi,
                    rent: state.eligibilityCheckForm.obligations.rent,
                    dateOfBirth: this.user.dob,
                    pin: this.user.correspondence_address.pin_code,
                    city: this.user.correspondence_address.city,
                    state: this.user.correspondence_address.state
                });
            }
        }
    }

    public getErrorMessage(control: AbstractControl): string {
        const controlName = this.getControlName(control);
        const errors = this.allErrorMessages();
        const message = '';
        if (control.errors) {
            const occurredError = first(Object.keys(control.errors));
            const predefinedErrorsForControl = errors[controlName];
            const result = predefinedErrorsForControl.find(predefinedError => {
                return predefinedError.type === occurredError;
            });
            if (result) {
                return result.message;
            } else {
                Logger.log(`Could not find error message for error: ${occurredError}`);
            }
        }
        return message;
    }

    public async onSubmit(formGroup: FormGroup): Promise<void> {
        this.loading = true;

        FormGroupUtil.validateFormGroup(formGroup);
        if (formGroup.invalid) {
            Logger.log(`Form is invalid. Not proceeding with submission.`);
            return;
        }
        const dateOfBirth = new Date(formGroup.controls['dateOfBirth'].value);
        const pin = formGroup.controls['pin'].value;
        const city = formGroup.controls['city'].value;
        const state = formGroup.controls['state'].value;
        const userId = this.storageService.getItem(Keys.PROFILE_ID);
        const userAddress = new UserAddress(state, pin, city);
        await this.userAddressService.create(userAddress, userId);
        await this.userService.updateDateOfBirth(userId, dateOfBirth);
        const incomeFormGroup = <FormGroup>formGroup.controls.income;
        try {
            let loanAmount;

            if (this.channelPartner.isAdvanceEmiPartner()) {
                const requestedAmount = formGroup.controls.requestedAmount.value;
                const advanceEmiOptionSelected = formGroup.controls.advanceEmiOption.value;
                loanAmount = this.calculateEffectiveLoanAmount(requestedAmount, advanceEmiOptionSelected.advanceEmiOption);

                await this.performEligibilityCheck(incomeFormGroup, formGroup, userId, loanAmount, requestedAmount, advanceEmiOptionSelected);
                await this.router.navigate(['borrow/eligibility-check/advance-emi-options']);
            } else {
                loanAmount = formGroup.controls.loanAmount.value;
                await this.performEligibilityCheck(incomeFormGroup, formGroup, userId, loanAmount, undefined, undefined);
                await this.router.navigate(['borrow/eligibility-check/options']);
            }
        } catch (error) {
            Logger.error(`Faced an error while checking eligibility. ${error}`);
            this.submissionError = 'An error occurred while checking your eligibility. Please try again.';
        } finally {
            this.loading = false;
        }
    }

    public onUser(user: any) {
        if (user) {
            this.user = user;
            this.name = user.name;
            this.mobileNumber = user.mobile_no;
            this.checkEligibilityForm.patchValue({
                dateOfBirth: user.dob,
                pin: user.correspondence_address.pin_code,
                city: user.correspondence_address.city,
                state: user.correspondence_address.state
            });
        }
    }

    public maxDateOfBirth(): Date {
        return EligibilityCheckFormComponent.MaximumDateOfBirth;
    }

    public minDateOfBirth(): Date {
        return EligibilityCheckFormComponent.MinimumDateOfBirth;
    }

    requiredIncome(formGroup: FormGroup) {
        const isSalariedEmployee = formGroup.controls.employeeType.value === EmployeeType.Salaried;
        if (isSalariedEmployee && !formGroup.controls.salary.value) {
            formGroup.controls.salary.setErrors({required: true});
            formGroup.controls.annualIncome.setErrors(null);
            return {requiredIncome: true};
        } else if (!isSalariedEmployee && !formGroup.controls.annualIncome.value) {
            formGroup.controls.salary.setErrors(null);
            formGroup.controls.annualIncome.setErrors({required: true});
            return {requiredIncome: true};
        }
    }

    onEmployeeTypeChanged(value: EmployeeType, formGroup: FormGroup) {
        (<FormGroup>formGroup.controls.income).controls.amount.reset();
    }

    onRequestedAmountChanged(value: string, formGroup: FormGroup) {
        (<FormGroup>formGroup.controls.advanceEmiOption).reset();
    }

    onClearCheckEligibilityFormInfo(): void {
        this.clearPhoneInfo = true;
        this.checkEligibilityForm.reset();
        this.storageService.clearSessionAndLocalStorage();
    }

    onResetClearPhoneInfo(): void {
        this.clearPhoneInfo = false;
    }

    isUserOnline(): boolean {
        return this.browserUtil.isOnline();
    }

    public scroll() {
        const element = this.document.getElementById('faq-section');
        element.scrollIntoView();
    }

    protected calculateEffectiveLoanAmount(requestedAmount: number, advanceEmiOption: AdvanceEmiOption): number {
        const effectiveLoanAmount = requestedAmount * (advanceEmiOption.totalTenure - advanceEmiOption.advanceEmiCount) / advanceEmiOption.totalTenure;
        return MathUtil.roundUpToNearestMultiple(effectiveLoanAmount, 1000);
    }

    private async performEligibilityCheck(incomeFormGroup: FormGroup, formGroup: FormGroup, userId: string, loanAmount: number, requestedAmount?: number, advanceEmiOptionSelected?: any) {
        const employeeType = incomeFormGroup.controls.employeeType.value;
        const incomeAmount = incomeFormGroup.controls.amount.value;
        const income: Income = employeeType === EmployeeType.Salaried ? new Income(incomeAmount, IncomeType.Monthly) :
            new Income(incomeAmount, IncomeType.Annual);
        const existingEmi = formGroup.controls.emi.value;
        const rent = formGroup.controls.rent.value;
        try {
            const obligations = new Obligations(rent, existingEmi);
            const request = new EligibilityCheckRequest(userId, this.channelPartner.id, loanAmount, income, obligations, 36, employeeType, requestedAmount, advanceEmiOptionSelected ? advanceEmiOptionSelected.advanceEmiOption : undefined);
            const eligibilityCheck = await this.eligibilityCheckService.checkEligibility(request);
            const creditOptions = await this.creditAssessmentService.getOptionsByAssessmentId(eligibilityCheck.getCreditAssessmentId());

            this.storageService.saveObject(Keys.EXTRA_OPTIONS_STATE, {
                creditOptions: creditOptions,
                eligibilityCheckForm: {
                    loanAmount: loanAmount,
                    requestedAmount: requestedAmount,
                    advanceEmiOption: advanceEmiOptionSelected ? advanceEmiOptionSelected.advanceEmiOption : undefined,
                    income: income,
                    obligations: obligations,
                    partnerId: this.channelPartner.id
                }
            });
        } catch (error) {
            Logger.error(`Faced an error while checking eligibility. ${error}`);
            this.submissionError = 'An error occurred while checking your eligibility. Please try again.';
        } finally {
            this.loading = false;
        }
    }

    private getControlName(c: AbstractControl): string | null {
        const formGroup = c.parent.controls;
        return Object.keys(formGroup).find(name => c === formGroup[name]) || null;
    }

    private maxForEmployeeType(employeeTypeControl: AbstractControl) {
        return (incomeAmountControl: AbstractControl) => {
            const type = employeeTypeControl.value;
            const minimumSalary = this.minimumSalary(type);
            if (incomeAmountControl.value < minimumSalary) {
                if (type === EmployeeType.SelfEmployed) {
                    return {
                        minAnnualIncome: 'Could not satisfy minimum salary requirements',
                    };
                } else {
                    return {
                        minMonthlyIncome: 'Could not satisfy minimum salary requirements',
                    };
                }
            } else {
                return null;
            }
        };
    }

    private minimumSalary(type) {
        let minimumSalary = 0;
        switch (type) {
            case EmployeeType.Salaried:
                minimumSalary = EligibilityCheckFormComponent.MinimumSalary;
                break;
            case EmployeeType.SelfEmployed:
                minimumSalary = EligibilityCheckFormComponent.MinimumAnnualSalary;
                break;
        }
        return minimumSalary;
    }

    private checkForAdvanceEmi() {
        if (this.channelPartner.advanceEmiPreferences) {
            this.channelPartner.advanceEmiPreferences.forEach(preference => {
                const advanceEmiDisplayOption = `${preference.totalTenure} EMIs${(preference.advanceEmiCount > 0 ? ` / ${preference.advanceEmiCount} Advance EMIs` : ``)}`;
                this.advanceEmiOptions.push({
                    advanceEmiDisplayOption: advanceEmiDisplayOption,
                    advanceEmiOption: preference
                });
            });
        }
    }

    private minimumBalanceAmount(requestedAmountFormControl: AbstractControl) {
        return (advanceEmiAbstractControl: AbstractControl) => {
            const advanceEmiOptionSelected = advanceEmiAbstractControl.value;
            if (advanceEmiOptionSelected) {
                const balanceAmount = this.calculateEffectiveLoanAmount(requestedAmountFormControl.value, advanceEmiOptionSelected.advanceEmiOption);
                if (balanceAmount < this.minimumLoanAmount) {
                    return {
                        minimumBalanceAmount: 'Could not satisfy minimum balance loan amount requirements',
                    };
                } else {
                    return null;
                }
            } else {
                return null;
            }
        };
    }
}
